/**
 * ポータル: チャット（船員アプリ）
 * ルーム一覧 → 会話の2画面切替。5秒ポーリング。1対1の新規作成に対応（グループ作成は陸上から）。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { AppHeader } from '../../components/AppHeader';
import { PIcon } from '../../components/PIcon';
import { BottomNav } from '../../components/BottomNav';
import { apiFetch } from '../../services/api';
import type { AuthUser } from '../../hooks/useAuth';
import { fmtDateTime, fileUrl, inputStyle, EmptyState } from './ui';

interface Room { id: string; room_type: string; name: string | null; member_count: number; unread: number; last_message: { body: string | null; sender_name: string | null; created_at: string } | null }
interface Msg { id: string; sender_user_id: string | null; sender_name: string | null; body: string | null; attachment_id: string | null; attachment_name?: string | null; created_at: string }
interface UserOpt { id: string; user_name: string }

export default function PortalChatPage({ user }: { user: AuthUser }) {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [roomName, setRoomName] = useState('');
  const [input, setInput] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [users, setUsers] = useState<UserOpt[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<string | null>(null);
  activeRef.current = activeId;

  const loadRooms = useCallback(async () => {
    try { const r = await apiFetch<{ data: Room[] }>('/portal/chat/rooms'); setRooms(r.data ?? []); } catch { /* noop */ }
  }, []);

  const loadMessages = useCallback(async (roomId: string, markRead: boolean) => {
    try {
      const r = await apiFetch<{ data: { room: { room_type: string; name: string | null }; members: Array<{ user_id: string; user_name: string }>; messages: Msg[] } }>(`/portal/chat/rooms/${roomId}/messages`);
      if (activeRef.current !== roomId) return;
      setMessages(r.data.messages ?? []);
      if (r.data.room.room_type === 'dm') {
        const partner = r.data.members.find(m => m.user_id !== user.user_id);
        setRoomName(partner?.user_name ?? '');
      } else setRoomName(r.data.room.name ?? '');
      if (markRead) {
        await apiFetch(`/portal/chat/rooms/${roomId}/read`, { method: 'POST', body: JSON.stringify({}) });
        setRooms(rs => rs.map(x => x.id === roomId ? { ...x, unread: 0 } : x));
      }
    } catch { /* noop */ }
  }, [user.user_id]);

  useEffect(() => { void loadRooms(); }, [loadRooms]);
  useEffect(() => {
    const t = setInterval(() => { if (!activeRef.current) void loadRooms(); }, 10_000);
    return () => clearInterval(t);
  }, [loadRooms]);
  useEffect(() => {
    if (!activeId) return;
    void loadMessages(activeId, true);
    const t = setInterval(() => {
      const hadUnread = rooms.find(r => r.id === activeId)?.unread ?? 0;
      void loadMessages(activeId, hadUnread > 0);
    }, 5_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, loadMessages]);
  useEffect(() => { bottomRef.current?.scrollIntoView(); }, [messages.length]);

  const send = async () => {
    const text = input.trim();
    if (!text || !activeId) return;
    setInput('');
    try {
      await apiFetch(`/portal/chat/rooms/${activeId}/messages`, { method: 'POST', body: JSON.stringify({ body: text }) });
      await loadMessages(activeId, false);
    } catch (e) { alert(e instanceof Error ? e.message : '送信に失敗しました'); setInput(text); }
  };

  const openNew = async () => {
    setShowNew(true);
    try {
      const r = await apiFetch<{ data: UserOpt[] }>('/portal/chat/directory');
      setUsers((r.data ?? []).filter(u => u.id !== user.user_id));
    } catch { /* noop */ }
  };

  const startDm = async (partnerId: string) => {
    try {
      const r = await apiFetch<{ data: { id: string } }>('/portal/chat/rooms', { method: 'POST', body: JSON.stringify({ room_type: 'dm', member_ids: [partnerId] }) });
      setShowNew(false);
      await loadRooms();
      setActiveId(r.data.id);
    } catch (e) { alert(e instanceof Error ? e.message : '作成に失敗しました'); }
  };

  // ── 会話画面 ──
  if (activeId) {
    return (
      <div style={{ height: '100dvh', display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
        <AppHeader title={roomName || 'チャット'} back={false}
          right={
            <button onClick={() => { setActiveId(null); setMessages([]); void loadRooms(); }}
              style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 10, padding: '7px 12px', fontSize: 13, fontWeight: 700, color: '#334155', cursor: 'pointer' }}>
              ← 一覧
            </button>
          } />
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>
          {messages.map(m => {
            const mine = m.sender_user_id === user.user_id;
            return (
              <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                <div style={{ maxWidth: '78%' }}>
                  {!mine && <div style={{ fontSize: 10.5, color: '#64748b', marginBottom: 2 }}>{m.sender_name}</div>}
                  <div style={{
                    background: mine ? '#0f766e' : '#fff', color: mine ? '#fff' : '#0f172a',
                    border: mine ? 'none' : '1px solid #e2e8f0',
                    borderRadius: mine ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                    padding: '9px 12px', fontSize: 14, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  }}>
                    {m.attachment_id ? (
                      <a href={fileUrl(`/portal/chat/attachments/${m.attachment_id}`)} target="_blank" rel="noreferrer"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: mine ? '#d9efec' : '#0c5f59', fontWeight: 700 }}><PIcon name="paperclip" size={13} /> {m.attachment_name ?? 'ファイル'}</a>
                    ) : m.body}
                  </div>
                  <div style={{ fontSize: 10, color: '#64748b', marginTop: 2, textAlign: mine ? 'right' : 'left' }}>{fmtDateTime(m.created_at)}</div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
        <div style={{ padding: '10px 12px calc(10px + env(safe-area-inset-bottom))', background: '#fff', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 8 }}>
          <input style={{ ...inputStyle, flex: 1 }} value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void send(); }} placeholder="メッセージを入力" />
          <button onClick={() => void send()}
            style={{ border: 'none', background: '#0f766e', color: '#fff', borderRadius: 10, padding: '0 18px', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
            送信
          </button>
        </div>
      </div>
    );
  }

  // ── ルーム一覧 ──
  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
      <AppHeader title="チャット"
        right={
          <button onClick={() => void openNew()}
            style={{ border: 'none', background: '#0f766e', color: '#fff', borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
            ＋ 新規
          </button>
        } />
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px 90px' }}>
        {rooms.length === 0 ? (
          <EmptyState img="/portal/empty-chat.png" text="チャットはまだありません。「＋ 新規」から始められます" />
        ) : rooms.map(r => (
          <button key={r.id} onClick={() => setActiveId(r.id)}
            style={{
              display: 'block', width: '100%', textAlign: 'left', marginBottom: 8,
              background: '#fff', border: '1.5px solid #e4e9f0', borderRadius: 14, padding: '12px 14px', cursor: 'pointer',
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 14.5, fontWeight: r.unread > 0 ? 800 : 700, color: '#0f172a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.room_type === 'group' ? <><span style={{ display: 'inline-block', verticalAlign: -2, marginRight: 5 }}><PIcon name="users" size={14} color="#0c5f59" /></span>{r.name}</> : r.name}
              </span>
              {r.unread > 0 && (
                <span style={{ background: '#dc2626', color: '#fff', borderRadius: 999, fontSize: 11, fontWeight: 700, padding: '2px 8px' }}>{r.unread}</span>
              )}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.last_message ? `${r.last_message.sender_name ?? ''}: ${r.last_message.body ?? ''}` : '（メッセージなし）'}
            </div>
          </button>
        ))}
      </div>

      {showNew && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end' }}
          onClick={() => setShowNew(false)}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '18px 18px 0 0', width: '100%', maxHeight: '70dvh', overflowY: 'auto', padding: '18px 18px 28px' }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 12 }}>相手を選んでチャットを開始</div>
            {users.map(u => (
              <button key={u.id} onClick={() => void startDm(u.id)}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 10px', border: 'none', borderBottom: '1px solid #f1f5f9', background: 'none', fontSize: 14.5, fontWeight: 600, color: '#0f172a', cursor: 'pointer' }}>
                {u.user_name}
              </button>
            ))}
            <p style={{ fontSize: 11.5, color: '#64748b', marginTop: 10 }}>グループチャットの作成・メンバー追加は陸上管理サイトから行えます。</p>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
